import type { AxiosInstance } from "axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAxios } from "@/contexts/AxiosContext";
import type { StyleListItem } from "@/types/api";
import type { DefaultStyleResponse, DefaultStyleUpdateBody } from "@/types/project";
import { getErrorMessage, throwLockAwareError } from "@/api/apiClient";

/** GET /{projectId}/style — list style DNA summaries (readme § List styles for a project). */
const fetchStyleList = async (axiosClient: AxiosInstance, projectId: string): Promise<readonly StyleListItem[]> => {
  try {
    const { data } = await axiosClient.get<readonly StyleListItem[] | StyleListItem[] | unknown>(`/${encodeURIComponent(projectId)}/style`);
    return Array.isArray(data) ? data : [];
  } catch (error) {
    throw new Error(getErrorMessage(error, "Something went wrong when listing styles."));
  }
};

export const fetchStyle = async (axiosClient: AxiosInstance, projectId: string, styleDnaId: string): Promise<DefaultStyleResponse> => {
  try {
    const response = await axiosClient.get<DefaultStyleResponse>(`/${encodeURIComponent(projectId)}/style/${encodeURIComponent(styleDnaId)}`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, "Something went wrong when loading the style document."));
  }
};

const updateStyle = async (
  axiosClient: AxiosInstance,
  projectId: string,
  styleDnaId: string,
  body: DefaultStyleUpdateBody
): Promise<{ status: "ok" }> => {
  try {
    const response = await axiosClient.post<{ status: "ok" }>(`/${encodeURIComponent(projectId)}/style/${encodeURIComponent(styleDnaId)}`, body);
    return response.data;
  } catch (error) {
    return throwLockAwareError(error, "Something went wrong when updating the style document.");
  }
};

// ===== React Query hooks =====

/** GET /{projectId}/style — all style DNA summaries for a project. */
export const useStyleList = (projectId: string | null, options?: { readonly enabled?: boolean }) => {
  const axiosClient = useAxios();
  const extraEnabled = options?.enabled !== false;
  return useQuery({
    queryKey: ["style", projectId, "list"],
    queryFn: async () => fetchStyleList(axiosClient, projectId!),
    enabled: typeof projectId === "string" && projectId.length > 0 && extraEnabled,
    retry: 1,
    refetchOnWindowFocus: false
  });
};

/**
 * GET /{projectId}/style/{styleDnaId} — full style document. Pass `styleDnaId` from
 * `project["style-dna-id"]` or a completed Style DNA Extraction transaction (`style_dna_id`).
 */
export const useStyle = (projectId: string | null, styleDnaId: string | null, options?: { readonly enabled?: boolean }) => {
  const axiosClient = useAxios();
  const extraEnabled = options?.enabled !== false;
  return useQuery({
    queryKey: ["style", projectId, styleDnaId],
    queryFn: async () => fetchStyle(axiosClient, projectId!, styleDnaId!),
    enabled: typeof projectId === "string" && projectId.length > 0 && typeof styleDnaId === "string" && styleDnaId.length > 0 && extraEnabled,
    retry: 1,
    refetchOnWindowFocus: false
  });
};

export const useUpdateStyle = () => {
  const axiosClient = useAxios();
  const queryClient = useQueryClient();
  return useMutation({
    async mutationFn(vars: { readonly projectId: string; readonly styleDnaId: string; readonly body: DefaultStyleUpdateBody }) {
      return updateStyle(axiosClient, vars.projectId, vars.styleDnaId, vars.body);
    },
    onSuccess(_, vars) {
      queryClient.setQueryData<DefaultStyleResponse>(["style", vars.projectId, vars.styleDnaId], (previous) => {
        if (previous === undefined) {
          return previous;
        }

        return { ...previous, ...vars.body };
      });
      void queryClient.invalidateQueries({ queryKey: ["style", vars.projectId] });
    }
  });
};
